import React, { useState, useEffect, useCallback } from 'react';
import './Survey.css';

interface Answer {
  [key: string]: string;
}

const Survey: React.FC = () => {
  const [answers, setAnswers] = useState<Answer>({});
  const [savedAnswers, setSavedAnswers] = useState<Answer>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [lastSaveInfo, setLastSaveInfo] = useState<{ filename: string, timestamp: string } | null>(null);

  const surveySections = [
    {
      section: 'Знакомство',
      items: [
        {
          id: '1.1',
          question: 'Вкратце расскажите о себе (Увлечения, хобби, участие в отрядах, мероприятиях)?',
          hint: 'Напишите пару предложений о том, чем вы живете вне учебы.'
        },
        {
          id: '1.2',
          question: 'Как бы ты оценил свою текущую вовлеченность в жизнь университета (от 1 до 10)?',
          hint: '1 — вообще не участвую, 10 — участвую во всем подряд.'
        },
        {
          id: '1.3',
          question: 'Какой тематики мероприятия вас чаще всего интересуют?',
          hint: 'Например: наука, спорт, музыка, волонтерство, карьера.'
        }
      ]
    },
    {
      section: 'Информирование',
      items: [
        {
          id: '2.1',
          question: 'Где ты обычно узнаёшь о событиях в вузе (чаты курса, группы вк, через знакомых, на сайтах)?',
          hint: 'Перечисли основные источники информации.'
        },
        {
          id: '2.2',
          question: 'Случалось ли так, что ты пропускал интересное мероприятие или набор в кружок просто потому, что узнал о нём слишком поздно или не нашел информацию? Если да, приведи пример.',
          hint: 'Расскажи подробнее о ситуации.'
        }
      ]
    },
    {
      section: 'Командная работа',
      items: [
        {
          id: '3.1',
          question: 'Нужна ли платформа в которой удобно объединяться и делать проекты?',
          hint: 'Да/Нет/Возможно. Почему?'
        },
        {
          id: '3.2',
          question: 'Когда вы готовите совместные проекты приходится ли вам делиться материалами? Вам удобнее демонстрировать материалы в реальном времени или отправлять файлы?',
          hint: 'Два вопроса в одном: нужен ли обмен файлами и как вы это делаете?'
        }
      ]
    },
    {
      section: 'Портфолио и достижения',
      items: [
        {
          id: '4.1',
          question: 'Каким образом вы храните (дипломы, гранты, достижения)?',
          hint: 'В папке на компе, в облаке, в ящике стола?'
        },
        {
          id: '4.2',
          question: 'Хотел бы ты, чтобы такой список формировался автоматически, и его можно было бы показать (например, прикрепить ссылку в резюме)?',
          hint: 'Оцени идею цифрового портфолио.'
        },
        {
          id: '4.3',
          question: 'Если бы крупные компании могли видеть твой профиль с достижениями (участие в мероприятиях, науке) и сами приглашать тебя на стажировку, как бы ты к этому отнёсся?',
          hint: 'Позитивно/Негативно/Сомневаюсь.'
        }
      ]
    },
    {
      section: 'Идеальная платформа',
      items: [
        {
          id: '5.1',
          question: 'Представь единую платформу, где собраны все активности универа (от науки до волонтерства), есть чат для команд и твой личный профиль с копилкой достижений. Какой бы функционал ты хотел бы в этом приложении.',
          hint: 'Опиши свои "хотелки" или "киллер-фичи".'
        },
        {
          id: '5.2',
          question: 'Что бы сподвигло тебя открывать её чаще?',
          hint: 'Уведомления, розыгрыши, актуальные новости, друзья там?'
        },
        {
          id: '5.3',
          question: 'Какую самую большую проблему для тебя закрывала бы такая интернет-платформа?',
          hint: 'Что бесит прямо сейчас, что хочется автоматизировать?'
        },
        {
          id: '5.4',
          question: 'Если бы ты мог добавить в эту платформу всего одну функцию, которая нужна именно тебе, чтобы это было?',
          hint: 'Одна, но самая важная функция.'
        }
      ]
    }
  ];

  const mapDbKeyToUi = (key: string) => key.replace(/^q/, '').replace('_', '.');
  const mapUiKeyToDb = (key: string) => `q${key.replace('.', '_')}`;


{/*

  const loadAnswers = useCallback(async () => {
    try {
      console.log('Загрузка ответов с сервера...');
      const response = await fetch('/api/answers');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const mappedAnswers = Object.entries(data).reduce<Answer>((acc, [key, value]) => {
        if (key.startsWith('q')) {
          acc[mapDbKeyToUi(key)] = value as string;
        }
        return acc;
      }, {});
      console.log('Ответы загружены:', mappedAnswers);
      setAnswers(mappedAnswers);
      setSavedAnswers(mappedAnswers);
    } catch (error) {
      console.error('Ошибка загрузки ответов:', error);
      setAnswers({});
      setSavedAnswers({});
    }
  }, []);

  // Загрузка сохраненных ответов при монтировании
  useEffect(() => {
    loadAnswers();
  }, [loadAnswers]);

*/}


  // Сброс статуса saved через 3 секунды
  useEffect(() => {
    if (saveStatus === 'saved') {
      const timer = setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [saveStatus]);



  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const saveAnswers = async () => {
    setIsSaving(true);
    setSaveStatus('saving');
    setErrorMessage('');
    
    try {
      console.log('1. Проверка подключения к серверу...');
      
      const healthCheck = await fetch('/api/health', {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
      }).catch(() => {
        throw new Error('Сервер не доступен. Убедитесь, что он запущен');
      });

      if (!healthCheck.ok) {
        throw new Error('Сервер вернул ошибку');
      }

      const healthData = await healthCheck.json();
      console.log('2. Сервер доступен:', healthData);

      console.log('3. Отправка ответов на сервер...');
      const payload = Object.entries(answers).reduce<Answer>((acc, [key, value]) => {
        acc[mapUiKeyToDb(key)] = value;
        return acc;
      }, {});
      console.log('📤 Данные для отправки:', payload);

      const response = await fetch('/api/save-answers', {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      console.log('4. Статус ответа:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const result = await response.json();
      console.log('5. Результат сохранения:', result);
      
      if (result.success) {
        setSavedAnswers({});
        setAnswers({});
        setSaveStatus('saved');
        setLastSaveInfo({
          filename: result.filename,
          timestamp: new Date().toLocaleTimeString()
        });
      } else {
        throw new Error(result.message || 'Ошибка сохранения');
      }
      
    } catch (error) {
      console.error('❌ Ошибка сохранения:', error);
      setSaveStatus('error');
      
      let errorMsg = 'Не удалось сохранить ответы. ';
      
      if (error instanceof Error) {
        if (error.message.includes('Сервер не доступен')) {
          errorMsg += 'Запустите сервер командой "npm start".';
        } else if (error.message.includes('Failed to fetch')) {
          errorMsg += 'Проверьте, что сервер запущен на порту 3000.';
        } else {
          errorMsg += error.message;
        }
      } else {
        errorMsg += 'Неизвестная ошибка';
      }
      
      setErrorMessage(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = JSON.stringify(answers) !== JSON.stringify(savedAnswers);

  return (
    <div className="background-gradient survey-page">
      <section className="section">
        <div className="container changelog-content">
          
          {/* Заголовок опросника */}
          <div className="changelog-header" style={{ marginBottom: '40px', textAlign: 'center' }}>
            <h1 style={{ fontSize: '2.5rem', color: 'white' }}>Опрос о потребностях студентов</h1>
            <p style={{ color: '#aaa', marginTop: '10px' }}>
              Нам важно твое мнение, чтобы создать идеальную платформу
            </p>
            
            {/* Статус сохранения */}
            <div style={{ marginTop: '20px' }}>
              {saveStatus === 'saving' && (
                <span style={{ color: '#ffaa00' }}>⏳ Сохранение на сервере...</span>
              )}
              {saveStatus === 'saved' && (
                <span style={{ color: '#00ff00' }}>✅ Ответы сохранены на сервере!</span>
              )}
              {saveStatus === 'error' && (
                <span style={{ color: '#ff0000' }}>❌ {errorMessage || 'Ошибка сохранения'}</span>
              )}
            </div>

            {/* Информация о последнем сохранении */}
            {lastSaveInfo && (
              <div style={{ 
                marginTop: '10px', 
                padding: '10px',
                background: 'rgba(120, 90, 220, 0.1)',
                borderRadius: '8px',
                border: '1px solid var(--card-accent-border)',
                fontSize: '0.9rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>📁</span>
                  <span style={{ color: 'var(--text-primary)' }}>Последнее сохранение:</span>
                </div>
                <div style={{ 
                  fontFamily: 'monospace', 
                  marginTop: '5px',
                  padding: '5px 10px',
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: '4px'
                }}>
                  {/* <div>Файл: <span style={{ color: '#9b8aff' }}>{lastSaveInfo.filename}</span></div>*/}
                  <div>Время: <span style={{ color: '#9b8aff' }}>{lastSaveInfo.timestamp}</span></div>
                </div>
              </div>
            )}
          </div>

          {/* Таймлайн с вопросами */}
          <div className="changelog-timeline">
            {surveySections.map((section, sectionIndex) => (
              <React.Fragment key={sectionIndex}>
                
                {/* Разделитель */}
                <div className="changelog-divider">
                  <div className="changelog-divider__top">
                    <span className="changelog-divider__line"></span>
                    <span className="changelog-divider__icon">
                      {sectionIndex === 0 && "👋"}
                      {sectionIndex === 1 && "📢"}
                      {sectionIndex === 2 && "👥"}
                      {sectionIndex === 3 && "💎"}
                      {sectionIndex === 4 && "💭"}
                    </span>
                    <span className="changelog-divider__line"></span>
                  </div>
                  <span className="changelog-item__date-text changelog-divider__text">{section.section}</span>
                </div>

                {/* Вопросы секции */}
                {section.items.map((item) => (
                  <div key={item.id} className="changelog-item">
                    
                    {/* Левая часть с ID вопроса */}
                    <div className="changelog-item__date question-date">
                      <span className="changelog-item__emoji question-emoji">❔</span>
                      <span className="changelog-item__date-text question-id">{item.id}</span>
                    </div>

                    {/* Правая часть с текстом вопроса и полем для ответа */}
                    <div className="changelog-item__content card" style={{ padding: '1.5rem' }}>
                      <h3 className="changelog-item__title" style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>
                        {item.question}
                      </h3>
                      
                      <p className="changelog-item__description" style={{ 
                        fontStyle: 'italic', 
                        color: 'var(--text-secondary)', 
                        marginBottom: '1rem',
                        fontSize: '0.95rem'
                      }}>
                        💡 {item.hint}
                      </p>

                      {/* Поле для ввода ответа */}
                      <textarea
                        className="answer-input"
                        placeholder="Ваш ответ..."
                        value={answers[item.id] || ''}
                        onChange={(e) => handleAnswerChange(item.id, e.target.value)}
                        rows={4}
                        style={{
                          width: '100%',
                          padding: '12px',
                          borderRadius: '8px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid var(--card-accent-border)',
                          color: 'var(--text-primary)',
                          fontSize: '1rem',
                          resize: 'none',
                          fontFamily: 'inherit',
                          marginTop: '10px'
                        }}
                      />

                      {/* Индикатор несохраненных изменений */}
                      {answers[item.id] !== savedAnswers[item.id] && (
                        <div style={{ 
                          marginTop: '8px', 
                          fontSize: '0.85rem', 
                          color: '#ffaa00',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px'
                        }}>
                          <span>✏️</span> Несохраненные изменения
                        </div>
                      )}
                    </div>

                  </div>
                ))}

              </React.Fragment>
            ))}
          </div>

          {/* Дополнительная кнопка сохранения внизу */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginTop: '40px',
            marginBottom: '20px'
          }}>
            <button
              onClick={saveAnswers}
              disabled={isSaving || !hasChanges}
              className="submit-button"
              style={{
                padding: '16px 50px',
                fontSize: '1.2rem'
              }}
            >
              {isSaving ? '⏳ Сохранение...' : 
               !hasChanges && saveStatus === 'saved' ? '✅ Ответы сохранены на сервере' : 
               'Отправить ответы'}
            </button>
          </div>

          {/* Информация о количестве отвеченных вопросов */}
          <div style={{ 
            textAlign: 'center', 
            color: '#aaa',
            marginBottom: '30px',
            fontSize: '0.95rem'
          }}>
            Отвечено на {Object.keys(answers).length} из {
              surveySections.reduce((acc, section) => acc + section.items.length, 0)
            } вопросов
          </div>

        </div>
      </section>
    </div>
  );
};

export default Survey;